<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    fullscreen
  >
    <template v-slot:activator="{ props }">
      <div v-bind="props" @click.stop data-cy="list-podNodeItem-actions-sharedFilesList-activator">
        <slot></slot>
      </div>
    </template>

    <v-card data-cy="dialog-sharedFilesList-modal">
      <v-card-title class="d-flex justify-space-between" data-cy="dialog-sharedFilesList-title">
        {{$t("routes.node.requestedFileLinks")}}

        <v-btn icon @click.stop="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="overflow-y-auto mt-4">
        <v-data-iterator
          :items="getFilteredUploadlinks"
          :items-per-page="10"
          :footer-props="footerOptions"
          :custom-sort="customSort"
          :loading="isLoading"
          style="width: 100%;"
        >
          <template v-slot:loader>
            <v-col class="text-center">
              <v-progress-circular
                indeterminate
                size="70"
                width="7"
                color="primary"
                :aria-label="$t('routes.node.loadingRequestLinksList')"
              />
              <div>{{ $t('routes.node.loadingRequestLinksList') }}</div>
            </v-col>
          </template>
          <template v-slot:default="props">
            <div>
              <v-expansion-panels variant="accordion" class="custom-panels">
                <v-expansion-panel v-for="{ raw: item } in props.items" :key="item.code" class="bg-list">
                  <v-expansion-panel-title class="py-4">
                    <v-row>
                      <v-col cols="12" sm="6" md="7">
                        <div class="d-flex flex-column">
                          <!-- Node name and teamonly icon -->
                          <div class="d-flex align-center">
                            <span v-if="item.node">
                              <router-link
                                :to="'/node/'+item.node.id">{{ item.node.name }}</router-link>
                            </span>
                            <v-icon v-if="item.teamonly" color="primary" size="small" class="ml-2"
                                    :title="$t('common.teamonly')">mdi-account-lock-outline
                            </v-icon>
                          </div>

                          <!-- Options chips -->
                          <div v-if="enabledOptionsLabels(item).length > 0" class="mt-2">
                            <v-chip
                              v-for="(label, idx) in enabledOptionsLabels(item)"
                              :key="idx"
                              class="mr-2 my-1"
                              size="small"
                              variant="outlined"
                            >
                              {{ label }}
                            </v-chip>
                          </div>
                        </div>
                      </v-col>

                      <v-col cols="12" sm="3" md="2">
                        <div class="date-info" :class="{'text-error': isExpired(item)}">
                          {{ $t('routes.node.expiresAt') }}:
                          <div class="font-weight-medium">{{ isoToDatetime(utc(item.expireAt)) }}</div>
                        </div>

                        <UploadlinkUpdateDialog
                          v-if="canControlUploadlinks"
                          :uploadlink="item"
                        >
                          <v-btn variant="text" size="small" height="32px" color="primary" class="mr-2 mt-2">
                            <template v-slot:prepend>
                              <v-icon size="small" color="primary">mdi-pencil</v-icon>
                            </template>
                            <span class="text-primary">{{ $t('common.edit') }}</span>
                          </v-btn>
                        </UploadlinkUpdateDialog>
                      </v-col>

                      <v-col cols="12" sm="3" md="3" class="d-flex flex-column justify-end">
                        <div class="date-info">
                          {{ $t('routes.node.lastUpdatedAt') }}:
                          <div class="font-weight-medium">{{ isoToDatetime(utc(item.updatedat)) }}</div>
                        </div>

                        <UploadlinkDeleteDialog
                          v-if="canControlUploadlinks"
                          :uploadlink="item"
                          :key="`delete-${item.id}`"
                          v-model="deleteDialogStates[item.id]"
                        >
                          <v-btn variant="text" size="small" height="32px" color="error" class="mt-2">
                            <template v-slot:prepend>
                              <v-icon size="small" color="error">mdi-delete</v-icon>
                            </template>
                            <span class="text-error">{{ $t('common.delete') }}</span>
                          </v-btn>
                        </UploadlinkDeleteDialog>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-title>

                  <v-expansion-panel-text>
                    <v-card flat class="pa-3">
                      <!-- URL Section -->
                      <div class="url-section mb-4">
                        <div class="d-flex flex-row align-end">
                          <div class="flex-grow-1">
                            <div class="text-subtitle-2 mb-1">{{ $t('routes.node.URL') }}</div>
                            <div class="url-display pa-2 rounded-lg d-flex"
                                 :class="this.theme.name === 'dark' ? 'dark' : 'light'"
                                 style="font-family: monospace; word-break: break-all;"
                                 @dblclick="selectAndCopyURLToClipboard($event, item)">
                              <div class="flex-grow-1 d-flex align-center">{{ getItemUrl(item) }}</div>
                              <div class="flex-grow-0">
                                <v-btn
                                  v-if="isCopyToClipboardSupported"
                                  size="small"
                                  density="comfortable"
                                  variant="text"
                                  width="32"
                                  height="32"
                                  rounded="lg"
                                  icon tile
                                  class="ml-2"
                                  @click="copyURLToClipboard(item)"
                                >
                                  <v-icon>mdi-content-copy</v-icon>
                                </v-btn>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Embedded Code Section -->
                      <div v-if="item?.params?.embeddable" class="code-section mb-4">
                        <div class="d-flex flex-row align-end">
                          <div class="flex-grow-1">
                            <div class="text-subtitle-2 mb-1">{{ $t('routes.node.code') }}</div>
                             <div class="d-flex code-display pa-2 rounded-lg"
                                 style="font-family: monospace; word-break: break-all;">
                              <div class="flex-grow-1 d-flex align-center">
                                <code @dblclick="selectAndCopyEmbeddedCodeToClipboard($event, item)">
                                  {{ getItemEmbeddedCode(item) }}
                                </code>
                              </div>
                              <div class="flex-grow-0">
                                <v-btn
                                  v-if="isCopyToClipboardSupported"
                                  size="small"
                                  density="comfortable"
                                  variant="text"
                                  width="32"
                                  height="32"
                                  rounded="lg"
                                  icon tile
                                  class="ml-2"
                                  @click="copyEmbeddedCodeToClipboard(item)"
                                >
                                  <v-icon>mdi-content-copy</v-icon>
                                </v-btn>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Password Section -->
                      <div v-if="item.password" class="password-section mb-4">
                        <div class="d-flex flex-row align-end">
                          <div class="flex-grow-1">
                            <div class="text-subtitle-2 mb-1">
                              {{ $t('routes.node.password') }}
                            </div>
                            <div
                              class="password-display pa-2 rounded-lg d-flex"
                              style="font-family: monospace; word-break: break-all;"
                              @dblclick="selectAndCopyPasswordToClipboard($event, item)"
                            >
                              <div class="flex-grow-1 d-flex align-center">
                                {{ item.password }}
                              </div>
                              <div class="flex-grow-0">
                                <v-btn
                                  v-if="isCopyToClipboardSupported"
                                  size="small"
                                  density="comfortable"
                                  variant="text"
                                  width="32"
                                  height="32"
                                  rounded="lg"
                                  icon tile
                                  @click="copyPasswordToClipboard(item)"
                                >
                                  <v-icon>mdi-content-copy</v-icon>
                                </v-btn>
                              </div>
                            </div>

                          </div>
                        </div>
                      </div>

                      <!-- Max File Size Section -->
                      <div v-if="item.params && item.params.maxfilesize"
                           class="filesize-section mb-4">
                        <div class="text-subtitle-2">{{ $t('menus.menuUploadLink.maxFilesize') }}
                        </div>
                        <div class="mt-1">{{ formatSize(item.params.maxfilesize, 0) }}</div>
                      </div>

                      <!-- Notes Section -->
                      <div v-if="getNotes(item)" class="notes-section mb-4">
                        <div class="text-subtitle-2 mb-1">Notes</div>
                        <div v-html="getNotes(item)" class="markdown-preview pa-3 rounded-lg"></div>
                      </div>

                      <!-- Invitations Section -->
                      <div v-if="item?.invitations?.length > 0" class="invitations-section mb-4">
                        <div class="text-subtitle-2 mb-2">
                          {{ $t('routes.pod.menuAccesslink.invitedPeople') }}
                        </div>
                        <div class="d-flex flex-wrap">
                          <v-chip
                            v-for="invitation in item.invitations"
                            :key="invitation.id"
                            size="small"
                            class="mr-2 mb-2"
                            color="primary"
                            variant="outlined"
                          >
                            <template v-slot:prepend>
                              <v-icon size="small">mdi-email-outline</v-icon>
                            </template>
                            {{ invitation.email }}
                          </v-chip>
                        </div>
                      </div>
                    </v-card>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </template>
        </v-data-iterator>
      </v-card-text>

      <v-card-actions>
        <v-checkbox
          v-model="listExpiredLinks"
          :label="$t('routes.node.listExpiredLinks')"
          hide-details
          class="mt-0 pt-0"
        />

        <v-select
          v-model="sortBy"
          :items="sortByItems"
          item-title="name"
          :placeholder="$t('routes.node.sortBy')"
          item-value="id"
          variant="solo-filled"
          max-width="260px"
          hide-details
          flat
        />

        <v-spacer></v-spacer>
        <v-btn variant="text" @click.stop="dialog = false">{{$t('common.close')}}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { useTheme } from 'vuetify';
import { useUploadLinkStore } from '@/stores/UploadLinkStore.js';
import UploadlinkUpdateDialog from './UploadlinkUpdateDialog.vue';
import UploadlinkDeleteDialog from './UploadlinkDeleteDialog.vue';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import { formatSize, isoToDatetime } from '@/components/formatters.js';
import DialogModel from '@/components/mixin/DialogModel.js';
import { utc } from '@date-fns/utc';

export default {
  mixins: [DialogActionKeys, DialogModel],
  name: "UploadlinksListDialog",
  components: {
    UploadlinkUpdateDialog,
    UploadlinkDeleteDialog,
  },
  data() {
    const theme = useTheme();

    return {
      listExpiredLinks: false,
      isLoading: false,
      sortBy: null,
      theme: theme,
      deleteDialogStates: {},
    };
  },
  watch: {
    dialog(newValue) {
      if (newValue) {
        this.refreshUploadLinks();
      }
    },
  },
  methods: {
    utc,
    isoToDatetime,
    formatSize,
    async refreshUploadLinks() {
      this.isLoading = true;
      try {
        await useUploadLinkStore().refreshUploadLinks();
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },
    customSort(items) {
      // probably do some sorting in the future
      return items;
    },
    selectAndCopyPasswordToClipboard(event, item) {
      const passwordSpan = event.target;

      const range = document.createRange();
      range.selectNodeContents(passwordSpan);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);

      if (this.isCopyToClipboardSupported) {
        this.copyPasswordToClipboard(item);
      }
    },
    copyPasswordToClipboard(item) {
      if (navigator && navigator.clipboard) {
        navigator.clipboard.writeText(item.password)
          .then(() => {
            const localizedMessage = this.$t('routes.notifications.passwordCopiedToClipboard');
            this.$showMessage(localizedMessage);

            console.log('The password was copied to the clipboard');
          })
          .catch((error) => {
            const localizedMessage = this.$t('routes.notifications.passwordNotCopiedToClipboard');
            this.$showMessage(localizedMessage, { type: 'error' });

            console.error('Failed to copy the password to the clipboard:', error);
          });
      }
    },
    selectAndCopyURLToClipboard(event, item) {
      const urlSpan = event.target;

      const range = document.createRange();
      range.selectNodeContents(urlSpan);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);

      if (this.isCopyToClipboardSupported) {
        this.copyURLToClipboard(item);
      }
    },
    selectAndCopyEmbeddedCodeToClipboard(event, item) {
      const span = event.target;

      const range = document.createRange();
      range.selectNodeContents(span);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);

      if (this.isCopyToClipboardSupported) {
        this.copyEmbeddedCodeToClipboard(item);
      }
    },
    copyURLToClipboard(item) {
      if (navigator && navigator.clipboard) {
        const url = this.getItemUrl(item);
        navigator.clipboard.writeText(url)
          .then(() => {
            const localizedMessage = this.$t('routes.notifications.urlCopiedToClipboard');
            this.$showMessage(localizedMessage);

            console.log('URL copied to clipboard:', url);
          })
          .catch((error) => {
            const localizedMessage = this.$t('routes.notifications.urlNotCopiedToClipboard');
            this.$showMessage(localizedMessage, { type: 'error' });

            console.error('Failed to copy URL to clipboard:', error);
          });
      }
    },
    copyEmbeddedCodeToClipboard(item) {
      if (navigator && navigator.clipboard) {
        const url = this.getItemEmbeddedCode(item);
        navigator.clipboard.writeText(url)
          .then(() => {
            const localizedMessage = this.$t('menus.menuUploadLink.copiedToClipboard');
            this.$showMessage(localizedMessage);
          })
          .catch(() => {
            const localizedMessage = this.$t('menus.menuUploadLink.notCopiedToClipboard');
            this.$showMessage(localizedMessage, { type: 'error' });
          });
      }
    },
    sortLinksByNodeName(links) {
      return links.sort((a, b) => {
        const nameA = a.node.name.toLowerCase();
        const nameB = b.node.name.toLowerCase();

        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });
    },
    sortLinksByExpirationDate(links) {
      return links.sort((a, b) => {
        const dateA = new Date(a.expireAt);
        const dateB = new Date(b.expireAt);

        if (dateA < dateB) return -1;
        if (dateA > dateB) return 1;
        return 0;
      });
    },
    enabledOptionsLabels(item) {
      const enabledOptions = [];

      if (item?.password) enabledOptions.push(this.$t('menus.menuUploadLink.passwordProtected'));
      if (item?.params?.embeddable) enabledOptions.push(this.$t('menus.menuUploadLink.asEmbeddableWidget'));

      return enabledOptions;
    },
    isExpired(item) {
      return new Date(item.expireAt) < new Date();
    },
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },
    getUploadlinks() {
      return useUploadLinkStore().getUploadLinks;
    },
    filterExpiredLinks() {
      if (this.listExpiredLinks) {
        return this.getUploadlinks;
      }

      const nowDate = new Date();

      return this.getUploadlinks.filter((link) => {
        const linkDate = new Date(link.expireAt);
        return linkDate > nowDate;
      });
    },
    getFilteredUploadlinks() {
      if (!this.sortBy) {
        return this.filterExpiredLinks;
      }

      let items = this.filterExpiredLinks.slice();

      switch (this.sortBy) {
        case 1:
        case 2:
          items = this.sortLinksByNodeName(items);
          break;
        case 3:
        case 4:
          items = this.sortLinksByExpirationDate(items);
          break;
        default:
          break;
      }

      if (this.sortBy % 2 === 0) {
        return items.reverse();
      }

      return items;
    },
    footerOptions() {
      return {
        'items-per-page-options': [10, 20, 30, 50]
      };
    },
    getItemUrl() {
      return (item) => {
        return window.location.origin + item.url;
      };
    },
    getItemEmbeddedCode() {
      return (item) => {
        if (item && item.code) {
          return `<iframe
          src="${window.location.origin}/embedded-upload/${item.code}"
          title="twUploadFrame"
          scrolling="auto"
          width="420px"
          height="400px"
          frameBorder="0" />`.replace(/\n/g, '');
        }
        return '';
      };
    },
    sortByItems() {
      return [
        {id: 1, name: this.$t('routes.node.folderNameAsc')},
        {id: 2, name: this.$t('routes.node.folderNameDesc')},
        {id: 3, name: this.$t('routes.node.expirationDateAsc')},
        {id: 4, name: this.$t('routes.node.expirationDateDesc')},
      ];
    },
    getExpireDateStyle() {
      const nowDate = new Date();

      return (item) => {
        const linkDate = new Date(item.expireAt);
        const styles = {};

        if (linkDate < nowDate) {
          styles.color = "red";
        }

        return styles;
      };
    },
    getNotes() {
      return (item) => {
        if (item && item.note) {
          return item.note;
        }

        return '';
      };
    },
    isCopyToClipboardSupported() {
      return navigator && navigator.clipboard;
    },
    canControlUploadlinks() {
      return this.$canListNodeAndPod() &&
        this.$canControlNodeAndPod() &&
        this.$canUploadFileAndAsset() &&
        this.$canControlPodService() &&
        this.$canListShareLinks() &&
        this.$canControlShareLinks();
    }
  }
};
</script>

<style>
.custom-panels {
  border-radius: 8px;
  overflow: hidden;
}

.date-info {
  font-size: 0.9rem;
}

.date-info div {
  font-size: 1.1rem;
  margin-top: 4px;
}

:deep(.v-theme--dark) .v-expansion-panel {
  background-color: #1E1E1E;
}

:deep(.v-theme--dark) .v-expansion-panel-title {
  background-color: #2D2D2D;
}

:deep(.v-theme--dark) .v-expansion-panel-text {
  background-color: #1E1E1E;
}

:deep(.v-theme--light) .v-expansion-panel {
  background-color: #FFFFFF;
}

:deep(.v-theme--light) .v-expansion-panel-title {
  background-color: #FAFAFA;
}

:deep(.v-theme--light) .v-expansion-panel-text {
  background-color: #FFFFFF;
}
</style>
